import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';

import ModalNew from './ModalNew';
import Button, {ButtonSizes} from './ButtonNew';
import {DiscordIcon} from './svg';
import {useAnalytics} from '../hooks';
import {cx} from '../util';

/** Shows the result of the Discord connection flow */

const ErrorMessages = {
  already_connected: 'That Discord account is already connected to another Aced account. Log in to Discord with a different account and try again.',
  denied: 'You cancelled the Discord authorization. You can connect again any time from Community in the menu.',
  impersonating: 'Discord cannot be connected while impersonating a user.',
};
const GENERIC_ERROR = 'Something went wrong connecting to Discord. Please try again, or contact support if it keeps happening.';

const DiscordConnectionModal = () => {
  const router = useRouter();
  const {tracker} = useAnalytics();
  const {discord, code} = router.query;
  const [outcome, setOutcome] = useState(null);
  const [open, setOpen] = useState(false);

  // Opens modal for a connection result, indicated by `discord` param, then strips params
  useEffect(() => {
    if (!router.isReady || !discord) return;

    if (discord === 'connected' || discord === 'error') {
      const connected = discord === 'connected';
      const errorCode = connected ? undefined : (code || 'discord');

      setOutcome({connected, code});
      setOpen(true);
      tracker.discordConnectionViewResult({connected, code: errorCode});
    }

    const {discord: _discord, code: _code, ...rest} = router.query;
    router.replace({pathname: router.pathname, query: rest}, undefined, {shallow: true});
  }, [router.isReady, discord, code]);

  const connected = Boolean(outcome?.connected);
  const canRetry = !connected && outcome?.code !== 'impersonating';

  const clickAction = (action) => {
    tracker.discordConnectionModalClick({action, connected});
    if (action === 'dismiss' || action === 'open') setOpen(false);
  };

  return (
    <ModalNew isOpen={open} onClose={() => clickAction('dismiss')} showCloseButton>
      <div className="text-center">
        <div className={cx('mx-auto flex h-14 w-14 items-center justify-center rounded-full', connected ? 'bg-indigo-100' : 'bg-red-100')}>
          <DiscordIcon className={cx('h-8 w-8', connected ? 'text-indigo-600' : 'text-red-600')} />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900">
          {connected ? 'You\'re in!' : 'Could not connect Discord'}
        </h2>
        <p className="mt-2 text-base text-gray-600">
          {connected
            ? 'Your Discord account is connected and you\'ve been added to our members-only community.'
            : ErrorMessages[outcome?.code] || GENERIC_ERROR}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          {connected && (
            <Button href="/discord?src=connected_modal" forceAnchor target="_blank" onClick={() => clickAction('open')} size={ButtonSizes.LARGE} className="w-full">
              Open Discord
            </Button>
          )}
          {canRetry && (
            <Button href="/discord?src=connection_error" forceAnchor onClick={() => clickAction('retry')} size={ButtonSizes.LARGE} className="w-full">
              Try again
            </Button>
          )}
        </div>
        {connected && (
          <p className="mt-4 text-sm text-gray-500">You can access it any time from Community in the menu.</p>
        )}
      </div>
    </ModalNew>
  );
};

export default DiscordConnectionModal;
